import gsap from "gsap";

export default class SwitchTabs {
    constructor(container = null) {
        if (!container) {
            this.initAllTabs();
            return;
        }

        this.container = container;
        this.tabsNav = this.container.querySelector('.tabs_nav');
        this.tabsNavItems = this.container.querySelectorAll('.tabs_nav-item');
        this.tabsContentPanels = this.container.querySelectorAll('.tabs_content-panel');
        this.isBlockSwitch2 = this.container.classList.contains('block-switch-2');
        this.backdrop = null;
        this.activeItem = null;
        this.hoverTimeline = null;
        this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        this.isInitialLoad = true;

        this.init();
    }

    initAllTabs() {
        document.querySelectorAll('.block-switch').forEach(block => {
            new SwitchTabs(block);
        });
    }

    init() {
        this.tabsNavItems.forEach(item => {
            if (this.isBlockSwitch2) {
                if (!this.isTouchDevice) {
                    item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
                    item.addEventListener('mouseleave', () => this.handleMouseLeave(item));
                }
            }
            item.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleTabClick(item);
            });
        });

        this.initDragScroll();

        if (this.isBlockSwitch2) {
            this.setupBackdrop();
        }

        if (this.tabsNavItems.length > 0) {
            // Vérifie si un hash d'URL cible un onglet de ce bloc
            const hashTab = this.getTabFromHash();

            if (this.isBlockSwitch2 && this.backdrop) {
                const firstItem = hashTab || this.tabsNavItems[0];
                firstItem.classList.add('active');
                this.activeItem = firstItem;

                this.setBackdropPositionWithoutAnimation(firstItem);

                setTimeout(() => {
                    gsap.to(this.backdrop, {
                        autoAlpha: 1,
                        duration: 0.3
                    });
                }, 100);

                const targetTab = firstItem.getAttribute('data-tab');
                const targetPanel = this.container.querySelector(`.tabs_content-panel[data-tab="${targetTab}"]`);
                if (targetPanel) {
                    targetPanel.classList.add('active');
                    gsap.to(targetPanel, {
                        opacity: 1,
                        duration: 0.5,
                        ease: "power2.inOut"
                    });
                }
            } else {
                (hashTab || this.tabsNavItems[0]).click();
            }

            // Si un hash ciblait ce bloc, scroll vers lui
            if (hashTab) {
                setTimeout(() => {
                    this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
            }

            this.isInitialLoad = false;
        }

        // Écoute les changements de hash en temps réel (clic sur un lien ancre)
        window.addEventListener('hashchange', () => this.onHashChange());
    }

    setupBackdrop() {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'tabs_nav-backdrop';
        this.tabsNav.appendChild(this.backdrop);

        gsap.set(this.backdrop, {
            position: 'absolute',
            backgroundColor: '#C3C3C3',
            borderRadius: '500px',
            zIndex: 1,
            top: '10px',
            left: 0,
            width: 0,
            height: 0
        });

        gsap.set(this.tabsNav, { position: 'relative' });

        this.tabsNavItems.forEach(item => {
            gsap.set(item, { position: 'relative', zIndex: 2 });
        });
    }

    setBackdropPositionWithoutAnimation(item) {
        const rect = item.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();
        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        gsap.set(this.backdrop, {
            x: offsetLeft,
            width: rect.width,
            height: rect.height,
            borderRadius: "500px"
        });
    }

    handleTabClick(clickedItem) {
        const targetTab = clickedItem.getAttribute('data-tab');

        this.tabsNavItems.forEach(navItem => navItem.classList.remove('active'));

        this.tabsContentPanels.forEach(panel => {
            panel.classList.remove('active');
            gsap.to(panel, {
                opacity: 0,
                duration: 0.5,
                ease: "power2.inOut"
            });
        });

        clickedItem.classList.add('active');
        this.activeItem = clickedItem;

        if (this.isBlockSwitch2 && this.backdrop) {
            if (this.hoverTimeline) {
                this.hoverTimeline.kill();
                this.hoverTimeline = null;
            }
            if (this.isInitialLoad) {
                this.setBackdropPositionWithoutAnimation(clickedItem);
            } else {
                this.animateBackdrop(clickedItem);
            }
        }

        const targetPanel = this.container.querySelector(`.tabs_content-panel[data-tab="${targetTab}"]`);
        targetPanel.classList.add('active');
        gsap.to(targetPanel, {
            opacity: 1,
            duration: 0.5,
            ease: "power2.inOut"
        });

        this.tabsNavItems.forEach(navItem => {
            navItem.querySelectorAll('.tabs_nav-item_borders-left, .tabs_nav-item_borders-right').forEach(border => border.remove());
        });

        // Ajoute les borders uniquement à l'item actif
        const activeBordersLeft = document.createElement('div');
        activeBordersLeft.className = 'tabs_nav-item_borders-left';
        const activeBordersRight = document.createElement('div');
        activeBordersRight.className = 'tabs_nav-item_borders-right';
        clickedItem.appendChild(activeBordersLeft);
        clickedItem.appendChild(activeBordersRight);
    }

    handleMouseEnter(hoveredItem) {
        if (this.isTouchDevice) return;
        if (hoveredItem === this.activeItem) return;

        if (this.hoverTimeline) {
            this.hoverTimeline.kill();
        }

        this.hoverTimeline = gsap.timeline();

        const rect = hoveredItem.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();
        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        this.hoverTimeline.to(this.backdrop, {
            x: offsetLeft,
            width: rect.width,
            height: rect.height,
            duration: 0.3,
            ease: "back.out(1.4)",
            borderRadius: "500px"
        });
    }

    handleMouseLeave(hoveredItem) {
        if (this.isTouchDevice) return;
        if (this.activeItem && hoveredItem !== this.activeItem) {
            if (this.hoverTimeline) {
                this.hoverTimeline.kill();
            }

            this.hoverTimeline = gsap.timeline();

            const rect = this.activeItem.getBoundingClientRect();
            const navRect = this.tabsNav.getBoundingClientRect();
            const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

            this.hoverTimeline.to(this.backdrop, {
                x: offsetLeft,
                width: rect.width,
                height: rect.height,
                duration: 0.4,
                ease: "power2.out",
                borderRadius: "500px"
            });
        }
    }

    /**
     * Ancre URL : détecte si le hash cible un onglet de ce bloc.
     * Formats supportés :
     *   #section-id--tab-slug   (ex: #gouvernance--coe)
     *   #tab-slug               (ex: #coe — match le premier bloc qui contient ce slug)
     */
    getTabFromHash() {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return null;

        const sectionId = this.container.getAttribute('id');

        if (hash.includes('--')) {
            const [blockId, tabSlug] = hash.split('--');
            // Le hash cible un bloc précis : on vérifie que c'est celui-ci
            if (sectionId && sectionId === blockId) {
                return this.findTabBySlug(tabSlug);
            }
            return null;
        }

        // Hash simple : on cherche le slug dans ce bloc
        return this.findTabBySlug(hash);
    }

    findTabBySlug(slug) {
        if (!slug) return null;
        for (const item of this.tabsNavItems) {
            if (item.getAttribute('data-tab-slug') === slug) {
                return item;
            }
        }
        return null;
    }

    onHashChange() {
        const hashTab = this.getTabFromHash();
        if (hashTab) {
            this.handleTabClick(hashTab);
            setTimeout(() => {
                this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }

    /**
     * Dev Mode : force le scroll horizontal même en desktop.
     * Activer depuis la console : window.switchTabsDevMode = true
     * Désactiver : window.switchTabsDevMode = false
     */
    applyDevMode() {
        const nav = this.tabsNav;
        if (!nav) return;

        nav.style.maxWidth = '100%';
        nav.style.overflowX = 'auto';
        nav.style.cursor = 'grab';
        nav.style.flexWrap = 'nowrap';
        console.log('%c[SwitchTabs DevMode] Activé — tabs_nav forcée à 400px max-width', 'color: #00DABE; font-weight: bold;');
    }

    removeDevMode() {
        const nav = this.tabsNav;
        if (!nav) return;

        nav.style.maxWidth = '';
        nav.style.overflowX = '';
        nav.style.cursor = '';
        nav.style.flexWrap = '';
        console.log('%c[SwitchTabs DevMode] Désactivé', 'color: #ff6b6b; font-weight: bold;');
    }

    initDragScroll() {
        const nav = this.tabsNav;
        if (!nav) return;

        // Dev mode : surveille window.switchTabsDevMode
        let devModeActive = false;
        const checkDevMode = () => {
            if (window.switchTabsDevMode && !devModeActive) {
                devModeActive = true;
                this.applyDevMode();
            } else if (!window.switchTabsDevMode && devModeActive) {
                devModeActive = false;
                this.removeDevMode();
            }
            requestAnimationFrame(checkDevMode);
        };
        requestAnimationFrame(checkDevMode);

        let isDown = false;
        let isDragging = false;
        let startX = 0;
        let scrollLeft = 0;

        nav.addEventListener('mousedown', (e) => {
            // Ignore si le contenu ne dépasse pas (pas de scroll nécessaire)
            if (nav.scrollWidth <= nav.clientWidth) return;

            isDown = true;
            isDragging = false;
            startX = e.pageX - nav.offsetLeft;
            scrollLeft = nav.scrollLeft;
        });

        nav.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();

            const x = e.pageX - nav.offsetLeft;
            const walk = x - startX;

            if (Math.abs(walk) > 5) {
                isDragging = true;
            }

            nav.scrollLeft = scrollLeft - walk;
        });

        nav.addEventListener('mouseup', () => {
            isDown = false;
        });

        nav.addEventListener('mouseleave', () => {
            isDown = false;
        });

        // Empêche le clic sur un onglet si on vient de draguer
        this.tabsNavItems.forEach(item => {
            item.addEventListener('click', (e) => {
                if (isDragging) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    isDragging = false;
                }
            }, true); // capture phase pour intercepter avant le handler principal
        });
    }

    animateBackdrop(clickedItem) {
        const rect = clickedItem.getBoundingClientRect();
        const navRect = this.tabsNav.getBoundingClientRect();

        const offsetLeft = rect.left - navRect.left + this.tabsNav.scrollLeft;

        gsap.timeline()
            .to(this.backdrop, {
                x: offsetLeft,
                width: rect.width,
                height: rect.height,
                duration: 0.4,
                ease: "back.out(1.7)",
                borderRadius: "500px"
            })
            .to(this.backdrop, {
                scaleX: 1.05,
                // scaleY: 0.95,
                duration: 0.1,
                ease: "sine.out",
            })
            .to(this.backdrop, {
                scaleX: 1,
                // scaleY: 1,
                duration: 0.2,
                ease: "elastic.out(1, 0.3)",
            });
    }
}